import React, { Component } from "react";
import styled, { css } from "styled-components";

import { Icon, IconButton, Tooltip } from "@material-ui/core";
import PresentationLibraryController, { PresentationFilterType, PresentationLibraryFilter, PresentationLibraryPresentation, PresentationLibraryControllerState } from "js/controllers/PresentationLibraryController";
import { FolderLocationBreadcrumb } from "js/editor/PresentationLibrary/components/FolderLocationBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { IWorkspaceResource } from "common/interfaces";
import { IWorkspaceResourceFolder } from "common/interfaces";
import getLogger from "js/core/logger";
import { getPresentation } from "js/core/models/presentation";
import { getPresentationContextMenuItems } from "js/editor/PresentationLibrary/PresentationContextMenu";
import AvatarStack, { AvatarStackProfile } from "js/editor/PresentationLibrary/components/AvatarStack";
import { fetchPresentationCollaboratorProfiles } from "js/editor/PresentationLibrary/utils/fetchPresentationCollaboratorProfiles";
import { app } from "js/namespaces";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { Divider } from "js/Components/Divider";
import { thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import {
    MultiSelectThumbnailGrid,
    Thumbnail,
    ThumbnailContainer
} from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { _ } from "js/vendor";
import ReadOnlyBadge from "./components/ReadOnlyBadge";
import { getLastAccessedLabel, LastActivityTooltipStyles } from "js/editor/PresentationLibrary/utils/getLastAccessedLabel";
import { getCjkUiFontFamilyCss } from "common/fontConstants";

const logger = getLogger();

const ThumbnailGridContainer = styled.div`
    margin: 0px 0px 20px;
    overflow: hidden;

    .dark-mode & {
        background: #333;
    }
`;

interface PresentationLibraryGridProps extends PresentationLibraryControllerState {
    onSelectedPresentations: (presentations: PresentationLibraryPresentation[]) => void;
    onDoubleClick: (presentation: PresentationLibraryPresentation) => void;
    onStartDragItems: (items: PresentationLibraryPresentation[]) => void;
    onDropDragItems: (items: PresentationLibraryPresentation[]) => void;
    readOnly: boolean;
    filter: PresentationLibraryFilter;
    resourceFolders: IWorkspaceResourceFolder[];
    resources: IWorkspaceResource[];
    onNavigateToFolder: (filter: { type: PresentationFilterType.FOLDER | PresentationFilterType.TEAM_FOLDER; folderId: string; subFolderId?: string }) => void;
}

export const PresentationLibraryGrid = PresentationLibraryController.withState(
    class PresentationLibraryGrid extends Component<PresentationLibraryGridProps> {
        render() {
            const { filteredPresentations, onSelectedPresentations, onDoubleClick, onStartDragItems, onDropDragItems, readOnly, resourceFolders, resources, onNavigateToFolder } = this.props;

            return (
                <ThumbnailGridContainer>
                    <LastActivityTooltipStyles />
                    <MultiSelectThumbnailGrid
                        items={filteredPresentations}
                        responsiveColumns
                        colGap={24}
                        rowGap={24}
                        readOnly={readOnly}
                        allowDragging={!readOnly}
                        allowMultiSelect={!readOnly}
                        thumbnailClass={PresentationLibraryPreviewThumbnail}
                        thumbnailProps={{
                            resourceFolders: resourceFolders ?? [],
                            resources: resources ?? [],
                            onNavigateToFolder: onNavigateToFolder ?? (() => {}),
                        }}
                        onItemSelected={items => onSelectedPresentations(items)}
                        onDoubleClick={onDoubleClick}
                        onStartDragItems={onStartDragItems}
                        onDropDragItems={onDropDragItems}
                        renderContextMenuItems={selectedPresentations => getPresentationContextMenuItems({ selectedPresentations })}
                    />
                </ThumbnailGridContainer>
            );
        }
    });

const ThumbnailBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 20px; 
    label {
        font-size: 14px;
        letter-spacing: 0px;
        color: #333;
        font-family: ${getCjkUiFontFamilyCss()};
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex: 1;
        min-width: 0;
        text-transform: none;
        margin: 0px;
    }
    button {
        padding: 2px;
    }

    .dark-mode & {
        label {
            color: #ddd;
        }
    }
`;

const AvatarStackWrap = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const ThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 0 10px;
`;

const LastAccessedLabel = styled.div`
    white-space: nowrap;
`;

const TruncatedText = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ThumbnailMetaRow = styled.div`
    display: flex;
    align-items: center;
    font-size: 10px;
    height: 20px;
`;

const PresentationThumbnailSelected = css`
    border: 1px solid #11A9E2;
    background: #D5E6ED;

    :hover {
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.01), 0 2px 2px 0 rgba(0, 0, 0, 0.01), 0 4px 4px 0 rgba(0, 0, 0, 0.02), 0 8px 8px 0 rgba(0, 0, 0, 0.03), 0 14px 14px 0 rgba(0, 0, 0, 0.03);
        cursor: pointer;
        border: 1px solid #11A9E2;
        background: #D5E6ED;
    }
`;

const PresentationThumbnail = css`
    border: 1px solid #DDD;
    background: #FFF;

    :hover {
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.01), 0 2px 2px 0 rgba(0, 0, 0, 0.01), 0 4px 4px 0 rgba(0, 0, 0, 0.02), 0 8px 8px 0 rgba(0, 0, 0, 0.03), 0 14px 14px 0 rgba(0, 0, 0, 0.03);
        cursor: pointer;
        border: 1px solid #DDD;
        background: #F6F9FB;
    }
`;

const PresentationThumbnailContainer = styled(ThumbnailContainer)<{ selected: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 10px;
    padding: 0px 0px 10px 0px;
    border-radius: 2px;
    overflow: auto;

    ${props => props.selected && PresentationThumbnailSelected}
    ${props => !props.selected && PresentationThumbnail}
`;

const ThumbnailMenuWrap = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    border-radius: 4px;
    // spinner is 1000, so we need to be above it
    z-index: 1001;
`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

const ReadOnlyBadgeContainer = styled.div`
    position:absolute;
    top: 8px;
    left: 8px;
    z-index: 100;
    align-items: center;
    justify-content: center;
    
`;

const PlaceholderContainer = styled.div`
    width: 100%;
`;

const PlaceholderImage = styled.div`
    width: 100%;
    aspect-ratio: 16/9;
`;

const PlaceholderLabel = styled.div`
    width: 100%;
    height: 24px;
    margin-top: 20px;
`;

interface PresentationLibraryPreviewThumbnailProps {
    item: PresentationLibraryPresentation;
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
    selected: boolean;
    readOnly: boolean;
    allSelected: PresentationLibraryPresentation[];
    onMenuClick?: (item: PresentationLibraryPresentation) => void;
    resourceFolders?: IWorkspaceResourceFolder[];
    resources?: IWorkspaceResource[];
    onNavigateToFolder?: (filter: { type: PresentationFilterType.FOLDER | PresentationFilterType.TEAM_FOLDER; folderId: string; subFolderId?: string }) => void;
}

interface PresentationLibraryPreviewThumbnailState {
    thumbnailUrl: string;
    visible: boolean;
    collaboratorProfiles: AvatarStackProfile[];
    isHovering: boolean;
}

export class PresentationLibraryPreviewThumbnail extends Component<PresentationLibraryPreviewThumbnailProps, PresentationLibraryPreviewThumbnailState> {
    ref: React.RefObject<HTMLDivElement>;
    observer: IntersectionObserver;

    constructor(props: PresentationLibraryPreviewThumbnailProps) {
        super(props);

        this.state = {
            thumbnailUrl: null,
            visible: false,
            collaboratorProfiles: [],
            isHovering: false,
        };

        this.ref = React.createRef();
    }

    fetchThumbnailUrl() {
        const { item: { getThumbnailUrl } } = this.props;
        const { thumbnailUrl: currentThumbnailUrl } = this.state;

        this.setState({ thumbnailUrl: null });

        getThumbnailUrl()
            .then(thumbnailUrl => this.setState({ thumbnailUrl }))
            .catch(err => {
                logger.error(err, "[PresentationLibraryPreviewThumbnail] presentationModel.getThumbnailUrl() failed");

                if (currentThumbnailUrl) {
                    // Restore previous thumbnail
                    this.setState({ thumbnailUrl: currentThumbnailUrl });
                }
            });
    }

    async fetchCollaboratorProfiles() {
        const { item } = this.props;
        try {
            const profiles = await fetchPresentationCollaboratorProfiles({
                workspaceId: item.workspaceId,
                resourceId: item.id,
                ownerUid: item.ownerUid,
            });
            if (this.ref.current) this.setState({ collaboratorProfiles: profiles });
        } catch (err) {
            logger.error(err, "[PresentationLibraryPreviewThumbnail] fetchCollaboratorProfiles failed");
        }
    }

    componentDidMount() {
        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                if (_.isEmpty(this.state.thumbnailUrl)) {
                    this.fetchThumbnailUrl();
                }
                if (!this.state.visible) {
                    this.setState({ visible: true });
                }
            } else {
                if (this.state.visible) {
                    this.setState({ visible: false });
                }
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentWillUnmount() {
        this.observer.disconnect();
    }

    componentDidUpdate(prevProps: PresentationLibraryPreviewThumbnailProps, prevState: PresentationLibraryPreviewThumbnailState) {
        if (prevProps.item.firstSlideModifiedAt !== this.props.item.firstSlideModifiedAt || prevProps.item.slideIds[0] !== this.props.item.slideIds[0]) {
            this.fetchThumbnailUrl();
        }
        if (!prevState.visible && this.state.visible && this.state.collaboratorProfiles.length === 0) {
            this.fetchCollaboratorProfiles();
        }
    }

    handleSharePresentation = async () => {
        const { item } = this.props;

        const presentation = await getPresentation(item.id, { permission: "write", autoSync: true });
        ShowDialog(ShareDialog, { presentation, onClose: () => presentation.disconnect() });
    }

    render() {
        const { item, onMouseDown, onMouseUp, onDoubleClick, onContextMenu, selected, readOnly, allSelected, resourceFolders, resources, onNavigateToFolder, onMenuClick } = this.props;
        const { thumbnailUrl, visible, collaboratorProfiles, isHovering } = this.state;
        const lastAccessedLabel = getLastAccessedLabel(item, true);
        const shouldShowOwnerAvatar = !item.lastActivity?.createdAt;
        // Synthetic owner row until collaborator fetch returns (avoids empty avatar on first paint)
        const avatarProfiles = shouldShowOwnerAvatar && collaboratorProfiles.length === 0
            ? [{
                displayName: item.ownerName,
                email: item.ownerUid,
                photoURL: item.ownerPhotoURL,
            }]
            : collaboratorProfiles;
        // AvatarStack deliberately hides a lone profile unless showSingleAvatar; after an edit we still often have only the owner in the stack
        const showSingleAvatarInGrid = avatarProfiles.length === 1;

        return (
            <PresentationThumbnailContainer
                selected={selected}
                data-item-id={item.id}
                className="thumbnail-grid-item"
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onDoubleClick={onDoubleClick}
                onContextMenu={onContextMenu}
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
                ref={this.ref}
            >
                {!visible && <PlaceholderContainer><PlaceholderImage /><PlaceholderLabel /></PlaceholderContainer>}
                {visible && <>
                    {isHovering && !app.isConstrained && !readOnly && (
                        <ThumbnailMenuWrap
                            onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onMouseUp={e => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onDoubleClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                if (allSelected.length > 0 && !allSelected.some(p => p.id === item.id)) {
                                    onMenuClick?.(item);
                                }
                            }}
                        >
                            <StyledIconMenu handleCloseMenu={() => this.setState({ isHovering: false })}>
                                {getPresentationContextMenuItems({
                                    selectedPresentations: allSelected.length > 0 && allSelected.some(p => p.id === item.id)
                                        ? allSelected
                                        : [item],
                                })}
                            </StyledIconMenu>
                        </ThumbnailMenuWrap>
                    )}
                    <div>
                        <Thumbnail
                        // used to prevent the box-shadow effect from being applied to the thumbnail
                            readOnly={true}
                            url={item.dirty ? null : thumbnailUrl}
                            showSpinner={true}
                        >
                            {(!item.permissions.owner && !item.permissions.write) && <ReadOnlyBadgeContainer><ReadOnlyBadge /></ReadOnlyBadgeContainer>}
                        </Thumbnail>
                        <Divider color="#DDD" />
                    </div>
                    <ThumbnailContent>
                        <ThumbnailBar>
                            <TruncatedTooltip title={item.name}>
                                <label className="presentation_item_name">{item.name}</label>
                            </TruncatedTooltip>
                            <AvatarStackWrap>
                                <AvatarStack
                                    isGrid
                                    profiles={avatarProfiles}
                                    showSingleAvatar={showSingleAvatarInGrid}
                                />
                            </AvatarStackWrap>
                            {
                                !window.isZoomApp &&
                            app.isMobileOrTablet &&
                            !readOnly &&
                            item.permissions.write &&
                            <IconButton
                                className="text-blue"
                                onMouseDown={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onMouseUp={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onDoubleClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    this.handleSharePresentation();
                                }}
                            >
                                <Icon>share</Icon>
                            </IconButton>
                            }
                        </ThumbnailBar>
                        <ThumbnailMetaRow>
                            {lastAccessedLabel && <>
                                <LastAccessedLabel>
                                    <Tooltip
                                        title={`${lastAccessedLabel?.actionLabel ?? ""} by ${lastAccessedLabel?.userName ?? ""}`}
                                        classes={{ tooltip: "last-activity-tooltip" }}
                                        open={lastAccessedLabel?.userName === "you" ? false : undefined}
                                    >
                                        <TruncatedText>
                                            {lastAccessedLabel?.label ?? ""}
                                        </TruncatedText>
                                    </Tooltip>
                                </LastAccessedLabel>
                            </>}

                            {resourceFolders && resources && onNavigateToFolder && (
                                <FolderLocationBreadcrumb
                                    showDivider={!!lastAccessedLabel}
                                    isGrid
                                    presentationId={item.id}
                                    resources={resources}
                                    resourceFolders={resourceFolders}
                                    onNavigateToFolder={onNavigateToFolder}
                                />
                            )}
                        </ThumbnailMetaRow>
                    </ThumbnailContent>
                </>}
            </PresentationThumbnailContainer>
        );
    }
}
